import { useQuery } from 'react-query'
import { format, startOfYesterday, subYears } from 'date-fns'
import { config } from 'config'
import { AuthContainer } from 'containers'
import { callAxios, CustomAxiosError } from 'utils/api'
import { getParamsForAll, getQueryParamsForAll } from 'utils/hooks'
import { reportingPeriodCheck } from 'utils/utilities'

const { SECURE_BASE_URL } = config!

export type GetSkuRequest = {
  vendorNumber: string
  division: string[]
  category: string[]
  subCategory: string[]
  brand: string[]
  reportingPeriod?: string
  startDate?: string
  endDate?: string
  reportName?: string
}

export type GetSkuResponse = {
  commercial_division: string
  commercial_category: string
  commercial_class: string
  brand_name: string
  sku_item_number: string
  sku_item_name: string
}[]

const getSku = async (props: GetSkuRequest): Promise<GetSkuResponse> => {
  const { vendorNumber, division, category, subCategory, brand, reportingPeriod, startDate, endDate, reportName } =
    props

  let queryParams = `vendornumber=${vendorNumber}`

  queryParams = getQueryParamsForAll(queryParams, 'commercial_division', division)
  queryParams = getQueryParamsForAll(queryParams, 'commercial_category', category)
  queryParams = getQueryParamsForAll(queryParams, 'commercial_class', subCategory)
  queryParams = getQueryParamsForAll(queryParams, 'brand', brand)

  if (reportingPeriod) {
    queryParams += `&reportingPeriod=${reportingPeriod}`
    if (reportingPeriod === 'Custom' && startDate && endDate)
      queryParams += `&startDate=${startDate}&endDate=${endDate}`
  } else {
    const lastYear = format(subYears(startOfYesterday(), 1), 'yyyy-MM-dd')
    const yesterday = format(startOfYesterday(), 'yyyy-MM-dd')
    queryParams += `&reportingPeriod=Custom&startDate=${lastYear}&endDate=${yesterday}`
  }
  if (reportName) queryParams += `&reportname=${reportName}`

  const { response: skuResponse } = await callAxios<{
    response: { data: GetSkuResponse }
  }>({
    url: `${SECURE_BASE_URL}/sku?${queryParams}`,
    method: 'GET',
  })

  const filteredSkuResponse = skuResponse.data?.filter((item) => !!item.sku_item_number && !!item.sku_item_name)

  return filteredSkuResponse
}

export const useGetSku = (props: Omit<GetSkuRequest, 'vendorNumber'>) => {
  const { division, category, subCategory, brand, reportName, reportingPeriod, startDate, endDate } = props
  const { selectedSupplier } = AuthContainer.useContainer()
  const vendorNumber = selectedSupplier?.oracleId ?? ''

  const isEnabled =
    !!vendorNumber &&
    reportingPeriodCheck(reportingPeriod, startDate, endDate) &&
    !!division.length &&
    !!category.length &&
    !!subCategory.length &&
    !!brand.length

  const {
    status: getSkuStatus,
    isFetching: getIsSkuLoading,
    data: skuData,
    error: getSkuError,
  } = useQuery<GetSkuResponse, CustomAxiosError>(
    [
      'sku/get',
      vendorNumber,
      getParamsForAll(division, 'only'),
      getParamsForAll(category, 'only'),
      getParamsForAll(subCategory, 'only'),
      getParamsForAll(brand, 'only'),
      reportName,
      reportingPeriod,
      startDate,
      endDate,
    ],
    () => getSku({ ...props, vendorNumber }),
    {
      enabled: isEnabled,
    }
  )

  return { getSkuStatus, getIsSkuLoading, skuData, getSkuError }
}
